(function () {
  const ONBOARDING_RETURN_KEY = "fiscal_onboarding_return_to";
  const PROFILE_PROPAGATION_DELAY_MS = 1000;
  const CHECKOUT_AFTER_LOGIN_KEY = "fiscal_checkout_after_login";
  const CHECKOUT_AFTER_LOGIN_MAX_AGE_MS = 30 * 60 * 1000;
  const DISCOUNT_COOKIE = "vestige_welcome_discount";
  const ONBOARDING_ATTEMPT_ID = window.crypto?.randomUUID?.()
    || `onb-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  // Eventi per GA4: il pixel personalizzato di Shopify li inoltra a GTM
  // (CUSTOM_EVENT_MAP del pixel: fiscal_onboarding_start / _complete).
  function publishAnalytics(name, data) {
    try {
      if (typeof window.Shopify?.analytics?.publish === "function") {
        window.Shopify.analytics.publish(name, data || {});
      }
    } catch (error) {}
  }

  // "Inizio" = primo campo toccato dal cliente, una volta per modulo.
  function publishStartOnFirstInput(container, data) {
    let sent = false;
    const send = () => {
      if (sent) return;
      sent = true;
      publishAnalytics("guestside:fiscal_onboarding_start", data());
    };
    container.addEventListener("input", send);
    container.addEventListener("change", send);
  }

  function reportOnboardingError(endpoint, shop, event, error, httpStatus) {
    if (!endpoint) return;
    fetch(endpoint, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        stage: "client_error",
        shop,
        attempt_id: ONBOARDING_ATTEMPT_ID,
        event,
        message: error?.name || String(error || "Errore frontend"),
        http_status: httpStatus || 0,
      }),
    }).catch(() => {});
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
  }

  // Re-apply the welcome discount right before checkout: Shopify's own click-tracking
  // association (set when the visitor first clicked the campaign email link) can be lost
  // across the login/fiscal-onboarding detour. DISCOUNT_COOKIE is our own first-party
  // fallback cookie for exactly that case (GuestSide sets it in email-login-nudge.js;
  // the other stores keep the same path ready, one cookie name per store).
  function goToDestination(destination) {
    const pendingDiscount = destination.indexOf("/checkout") !== -1 ? getCookie(DISCOUNT_COOKIE) : null;
    if (pendingDiscount) {
      deleteCookie(DISCOUNT_COOKIE);
      window.location.href = `/discount/${encodeURIComponent(pendingDiscount)}?redirect=${encodeURIComponent(destination)}`;
      return;
    }
    window.location.href = destination;
  }

  // Chi apre l'onboarding senza return_to (link diretto, dalla home) torna alla
  // pagina da cui e' arrivato, purche' sia di questo sito e non l'onboarding
  // stesso (il form contatti ricarica la pagina). Chi vuole /account lo chiede
  // esplicitamente col return_to (icona in alto, pagina account).
  function getReferrerPath() {
    try {
      if (!document.referrer) return "";
      const ref = new URL(document.referrer);
      if (ref.origin !== window.location.origin) return "";
      if (normalizePath(ref.pathname) === normalizePath(window.location.pathname)) return "";
      if (ref.pathname.indexOf("/checkout") === 0) return "";
      return `${ref.pathname}${ref.search}${ref.hash}`;
    } catch (err) {
      return "";
    }
  }

  function storedOnboardingReturn() {
    try { return sessionStorage.getItem(ONBOARDING_RETURN_KEY); } catch (error) { return ""; }
  }

  function getReturnTo(defaultUrl) {
    const params = new URLSearchParams(window.location.search);
    return params.get("return_to")
      || storedOnboardingReturn()
      || getReferrerPath()
      || defaultUrl
      || "/";
  }

  // Da quale porta e' entrato chi compila: l'icona in alto, la pagina account, la
  // fascia o il blocco al checkout. Ogni ingresso si annuncia con ?from=; chi
  // arriva senza e' "diretto" (link salvato, cronologia). Il PIM archivia il
  // payload cosi' com'e' in ViesCheckLog, quindi basta spedirlo.
  function getEntryPoint() {
    return new URLSearchParams(window.location.search).get("from") || "diretto";
  }

  function normalizePath(path) {
    return (path || "/").replace(/\/+$/, "") || "/";
  }

  function getPath(url) {
    return normalizePath(new URL(url, window.location.origin).pathname);
  }

  function isAllowedRedirectPath(config) {
    const currentPath = normalizePath(window.location.pathname);
    const isNewsletterPage = currentPath === "/pages/newsletter"
      || currentPath.endsWith("/pages/newsletter");
    const allowedPaths = [
      getPath(config.onboardingUrl),
      getPath(config.loginUrl),
      getPath(config.registerUrl),
      getPath(config.logoutUrl),
      "/challenge",
    ];

    return isNewsletterPage || allowedPaths.includes(currentPath);
  }

  // L'URL Liquid conosce il path, ma non sempre conserva la query string della
  // pagina. Subito prima del click completiamo il return_to con l'URL relativo
  // reale del browser, cosi' il login normale torna esattamente dove e' partito.
  function initCurrentPageLoginLinks() {
    document.querySelectorAll("[data-login-return-current]").forEach((link) => {
      link.addEventListener("click", () => {
        try {
          const loginUrl = new URL(link.href, window.location.origin);
          loginUrl.searchParams.set("return_to", `${window.location.pathname}${window.location.search}`);
          link.href = `${loginUrl.pathname}${loginUrl.search}`;
        } catch (error) {
          // Il link Liquid resta comunque un fallback valido al path corrente.
        }
      });
    });
  }

  function goToRequiredOnboarding(config) {
    const returnTo = "/checkout";
    try { sessionStorage.setItem(ONBOARDING_RETURN_KEY, returnTo); } catch (error) {}
    window.location.replace(`${config.onboardingUrl}?return_to=${encodeURIComponent(returnTo)}&from=checkout`);
  }

  // Chi torna dal checkout senza i dati completi ha visto il blocco di Boomgate,
  // il cui messaggio promette "clicca sul logo: verrai portato alla pagina giusta".
  // Finche' esisteva il reindirizzamento da ogni pagina la promessa si avverava da
  // sola; ora la si mantiene riconoscendo la provenienza dal checkout.
  function initCheckoutReturnRedirect() {
    const gate = document.querySelector("[data-fiscal-onboarding-gate]");
    if (!gate) return;
    if (gate.dataset.customerAuthenticated !== "true") return;
    if (gate.dataset.onboardingComplete === "true") return;
    if (window.Shopify?.designMode || isAllowedRedirectPath(gate.dataset)) return;

    let cameFromCheckout = false;
    try {
      cameFromCheckout = document.referrer
        ? new URL(document.referrer).pathname.indexOf("/checkout") === 0
        : false;
    } catch (err) {
      cameFromCheckout = false;
    }
    if (!cameFromCheckout) return;

    goToRequiredOnboarding(gate.dataset);
  }

  // Dopo un salvataggio riuscito la pagina si ricarica perche' Liquid rilegga il
  // tipo cliente. Shopify puo' metterci qualche secondo a esporlo: se si ricarica
  // prima, il modulo prezzi ricompare come se nulla fosse stato salvato e il
  // cliente lo rifa' (casi reali su Amonn: aziende con P.IVA valida salvate di
  // nuovo come privati). Il salvataggio resta in sessionStorage per qualche
  // minuto: in quel tempo il modulo non si riapre da solo, la pagina riprova a
  // ricaricarsi e, se il tipo resta invisibile, si mostra la fascia e lo si
  // segnala al PIM.
  const PRICING_SAVED_MAX_AGE_MS = 5 * 60 * 1000;
  const PRICING_SAVED_MAX_RELOADS = 2;
  const PRICING_SAVED_RETRY_DELAY_MS = 2500;

  function pricingSavedKey(customerId) {
    return `fiscal_pricing_saved:${customerId || ""}`;
  }

  function rememberPricingSaved(customerId, customerType) {
    try {
      sessionStorage.setItem(
        pricingSavedKey(customerId),
        JSON.stringify({ type: customerType, at: Date.now(), reloads: 0 })
      );
    } catch (err) { /* navigazione privata */ }
  }

  function readPricingSaved(customerId) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(pricingSavedKey(customerId)) || "null");
      if (saved && Date.now() - saved.at < PRICING_SAVED_MAX_AGE_MS) return saved;
    } catch (err) { /* navigazione privata o valore rovinato */ }
    return null;
  }

  function writePricingSaved(customerId, saved) {
    try { sessionStorage.setItem(pricingSavedKey(customerId), JSON.stringify(saved)); } catch (err) {}
  }

  function forgetPricingSaved(customerId) {
    try { sessionStorage.removeItem(pricingSavedKey(customerId)); } catch (err) {}
  }

  // Mini-modulo post-login: paese e tipo cliente (piu' la P.IVA per le aziende).
  // Sono le sole informazioni che decidono il prezzo esposto; il modulo completo
  // resta richiesto al checkout da initCheckoutGate.
  function initPricingOverlay() {
    const gate = document.querySelector("[data-fiscal-onboarding-gate]");
    const overlay = document.querySelector("[data-fiscal-pricing-overlay]");
    const banner = document.querySelector("[data-fiscal-banner]");
    if (gate && !overlay) forgetPricingSaved(gate.dataset.customerId);
    if (!gate || !overlay) return;
    if (window.Shopify?.designMode || isAllowedRedirectPath(gate.dataset)) return;

    // Per cliente: con una chiave unica il "piu' tardi" di un account valeva
    // anche per il successivo accesso con un altro account nella stessa sessione.
    const SEEN_KEY = `fiscal_pricing_overlay_seen:${gate.dataset.customerId || ""}`;
    const country = overlay.querySelector("[data-fo-mini-country]");
    const type = overlay.querySelector("[data-fo-mini-type]");
    const vatWrap = overlay.querySelector("[data-fo-mini-vat-wrap]");
    const vat = overlay.querySelector("[data-fo-mini-vat]");
    const vatPrefix = overlay.querySelector("[data-fo-mini-vat-prefix]");
    const errorBox = overlay.querySelector("[data-fo-mini-error]");
    const submit = overlay.querySelector("[data-fo-mini-submit]");
    const later = overlay.querySelector("[data-fo-mini-later]");
    const retry = overlay.querySelector("[data-fo-mini-retry]");

    // "automatico" e' l'apertura all'arrivo sulla pagina; diventa "fascia" solo se
    // e' stato il pulsante della fascia a riaprirlo dopo un "piu' tardi".
    let openedFrom = "automatico";
    let invalidViesSavedKey = "";
    publishStartOnFirstInput(overlay, () => ({ onboarding_form: "prezzi", entry_point: openedFrom }));

    function openOverlay() {
      overlay.hidden = false;
      if (banner) banner.hidden = true;
    }

    function closeOverlay() {
      overlay.hidden = true;
      if (banner) banner.hidden = false;
    }

    // Il prefisso IVA coincide con il codice paese tranne che per la Grecia, dove
    // e' EL: stessa conversione che fa normalize_vat lato PIM.
    function vatPrefixFor(countryCode) {
      return countryCode === "GR" ? "EL" : countryCode;
    }

    function syncVat() {
      const business = type.value !== "B2C";
      vatWrap.hidden = !business;
      if (!business) vat.value = "";
      if (vatPrefix) vatPrefix.textContent = business ? vatPrefixFor(country.value) : "";
    }

    function currentVatKey() {
      return `${country.value}|${type.value}|${vat.value.trim()}`;
    }

    function resetInvalidViesConfirmation() {
      invalidViesSavedKey = "";
      submit.textContent = submit.dataset.idleLabel;
      if (retry) retry.hidden = true;
    }

    function showError(message) {
      errorBox.classList.remove("fo-mini__error--pending", "fo-mini__error--valid");
      errorBox.textContent = message;
      errorBox.hidden = !message;
    }

    // Esiti VIES che non sono errori: stessa casella, colore diverso.
    function showNotice(message, kind) {
      showError(message);
      errorBox.classList.add(`fo-mini__error--${kind}`);
    }

    // Ripulisce mentre si digita: maiuscole, via separatori e spazi, e via il
    // prefisso paese se ripetuto (il campo deve contenere il solo numero).
    function cleanVat(raw, prefix) {
      let value = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      // Si toglie sia il prefisso IVA sia il codice ISO del paese: per la Grecia
      // sono diversi (EL e GR) e chi incolla puo' aver usato l'uno o l'altro.
      const codici = [prefix, country.value].filter(Boolean);
      for (const codice of codici) {
        if (value.startsWith(codice)) {
          value = value.slice(codice.length);
          break;
        }
      }
      return value;
    }

    vat.addEventListener("input", () => {
      resetInvalidViesConfirmation();
      const prefix = vatPrefixFor(country.value);
      const before = vat.value;
      const after = cleanVat(before, prefix);
      if (after === before) return;
      // Il cursore va riportato indietro di quanto abbiamo tolto prima di esso.
      const caret = vat.selectionStart || before.length;
      const removedBefore = before.slice(0, caret).length - cleanVat(before.slice(0, caret), prefix).length;
      vat.value = after;
      const pos = Math.max(0, caret - removedBefore);
      vat.setSelectionRange(pos, pos);
    });

    // Formato: l'italiana e' 11 cifre, per le altre ci si limita a intercettare
    // un numero palesemente incompleto. Il controllo vero resta il VIES.
    function vatFormatError(value) {
      const d = overlay.dataset;
      if (country.value === "IT") {
        if (!/^\d{11}$/.test(value)) return d.errorVatFormatIt;
        return partitaIvaValida(value) ? "" : d.errorVatChecksum || d.errorVatFormatIt;
      }
      return vatUeValido(value, country.value) ? "" : d.errorVatFormat;
    }

    type.addEventListener("change", () => {
      resetInvalidViesConfirmation();
      syncVat();
    });
    country.addEventListener("change", () => {
      resetInvalidViesConfirmation();
      // Cambiando paese il prefisso cambia: si ripulisce col nuovo.
      syncVat();
      vat.value = cleanVat(vat.value, vatPrefixFor(country.value));
    });
    syncVat();

    if (retry) {
      retry.addEventListener("click", () => {
        resetInvalidViesConfirmation();
        submit.click();
      });
    }

    later.addEventListener("click", () => {
      // La scelta vale per la sessione: si insiste al prossimo accesso, non ora.
      try { sessionStorage.setItem(SEEN_KEY, "1"); } catch (err) { /* privata */ }
      closeOverlay();
    });

    if (banner) {
      const opener = banner.querySelector("[data-fo-banner-open]");
      if (opener) {
        opener.addEventListener("click", () => {
          openedFrom = "fascia";
          openOverlay();
        });
      }
    }

    function setBusy(busy, label) {
      submit.disabled = busy;
      overlay.classList.toggle("is-busy", busy);
      submit.textContent = busy ? label : submit.dataset.idleLabel;
    }

    // La verifica VIES puo' arrivare a 15 secondi (timeout lato PIM): senza un
    // messaggio d'attesa esplicito sembra che il pulsante non abbia funzionato.
    function messageForFailure(data) {
      const d = overlay.dataset;
      if (data.status === "write_error") return d.errorWrite;
      if (data.vies_status === "invalid") return d.errorViesInvalid;
      if (data.vies_status === "unavailable") return d.errorViesUnavailable;
      const coherence = (data.errors || []).join(" ");
      if (coherence) return coherence;
      return d.genericError;
    }

    submit.dataset.idleLabel = submit.textContent.trim();

    submit.addEventListener("click", async () => {
      showError("");
      const business = type.value !== "B2C";
      if (business) {
        vat.value = cleanVat(vat.value, vatPrefixFor(country.value));
        if (!vat.value) {
          showError(overlay.dataset.errorVatRequired);
          vat.focus();
          return;
        }
        const formatError = vatFormatError(vat.value);
        if (formatError) {
          showError(formatError);
          vat.focus();
          return;
        }
      }
      if (business && invalidViesSavedKey === currentVatKey()) {
        setBusy(true, overlay.dataset.busyDone);
        rememberPricingSaved(gate.dataset.customerId, type.value);
        window.location.reload();
        return;
      }
      setBusy(true, business ? overlay.dataset.busyVies : overlay.dataset.busySaving);
      try {
        const response = await fetch(gate.dataset.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            stage: "pricing",
            attempt_id: ONBOARDING_ATTEMPT_ID,
            entry_point: openedFrom,
            customer_id: `gid://shopify/Customer/${gate.dataset.customerId}`,
            shop: window.location.host,
            country_code: country.value,
            eu_country: country.value,
            customer_type: type.value,
            vat_number: vat.value.trim(),
            eu_vat_id: vat.value.trim(),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && data.status === "ok") {
          publishAnalytics("guestside:fiscal_onboarding_complete", {
            onboarding_form: "prezzi",
            entry_point: openedFrom,
            customer_type: type.value,
            country_code: country.value,
            vies_status: data.vies_status || "",
          });
          // P.IVA respinta o VIES giu': i dati sono gia' salvati, ma il cliente deve
          // leggere l'esito prima che la pagina si ricarichi. Il secondo clic sullo
          // stesso numero ricarica (vedi invalidViesSavedKey).
          if (business && (data.vies_status === "invalid" || data.vies_status === "unavailable")) {
            const unavailable = data.vies_status === "unavailable";
            invalidViesSavedKey = currentVatKey();
            if (unavailable) showNotice(overlay.dataset.errorViesUnavailable, "pending");
            else showError(overlay.dataset.errorViesInvalid);
            setBusy(false);
            submit.textContent = unavailable ? overlay.dataset.viesContinue : overlay.dataset.viesProceed;
            if (retry) retry.hidden = !unavailable;
            return;
          }
          if (business && data.vies_status === "valid") {
            showNotice(overlay.dataset.viesValid, "valid");
            setBusy(true, overlay.dataset.busyDone);
            rememberPricingSaved(gate.dataset.customerId, type.value);
            setTimeout(() => window.location.reload(), 1500);
            return;
          }
          // I metafield sono scritti lato Shopify: serve un giro di pagina perche'
          // Liquid li rilegga e tax-context ricalcoli il prezzo.
          setBusy(true, overlay.dataset.busyDone);
          rememberPricingSaved(gate.dataset.customerId, type.value);
          setTimeout(() => window.location.reload(), PROFILE_PROPAGATION_DELAY_MS);
          return;
        }
        showError(messageForFailure(data));
      } catch (err) {
        // Rete assente o PIM irraggiungibile: la richiesta non e' nemmeno partita.
        reportOnboardingError(gate.dataset.endpoint, window.location.host, "pricing_request", err);
        showError(overlay.dataset.errorNetwork);
      }
      setBusy(false);
    });

    const forceOpen = new URLSearchParams(window.location.search).get("open_pricing") === "1";
    if (forceOpen) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("open_pricing");
      window.history.replaceState({}, "", `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    }
    const saved = forceOpen ? null : readPricingSaved(gate.dataset.customerId);
    if (saved) {
      if (saved.reloads < PRICING_SAVED_MAX_RELOADS) {
        if (saved.reloads === 0) {
          reportOnboardingError(gate.dataset.endpoint, window.location.host, "pricing_saved_not_visible_retry", { name: "pricing_saved_not_visible_retry" });
        }
        writePricingSaved(gate.dataset.customerId, { ...saved, reloads: saved.reloads + 1 });
        openOverlay();
        setBusy(true, overlay.dataset.busyDone);
        setTimeout(() => window.location.reload(), PRICING_SAVED_RETRY_DELAY_MS);
        return;
      }
      if (!saved.reported) {
        reportOnboardingError(gate.dataset.endpoint, window.location.host, "pricing_saved_not_visible_gave_up", { name: "pricing_saved_not_visible_gave_up" });
        writePricingSaved(gate.dataset.customerId, { ...saved, reported: true });
      }
      try { sessionStorage.setItem(SEEN_KEY, "1"); } catch (err) {}
      closeOverlay();
      return;
    }
    let alreadySeen = false;
    try { alreadySeen = sessionStorage.getItem(SEEN_KEY) === "1"; } catch (err) { alreadySeen = false; }
    if (alreadySeen && !forceOpen) {
      closeOverlay();
    } else {
      openOverlay();
    }
  }

  function goToOnboarding(config) {
    const returnTo = "/checkout";
    try { sessionStorage.setItem(ONBOARDING_RETURN_KEY, returnTo); } catch (error) {}

    if (config.customerAuthenticated === "true") {
      window.location.href = `${config.onboardingUrl}?return_to=${encodeURIComponent(returnTo)}&from=checkout`;
      return;
    }

    const onboardingAfterLogin = `${config.onboardingUrl}?return_to=${encodeURIComponent(returnTo)}&from=checkout`;
    // L'URL di login puo' avere gia' una query (es. ?region_country=IT): accodare
    // "?return_to=" la rompeva, il ritorno si perdeva e il cliente finiva sugli
    // ordini dell'account invece che sull'onboarding.
    const loginUrl = new URL(config.loginUrl, window.location.origin);
    loginUrl.searchParams.set("return_to", onboardingAfterLogin);
    // Chi si registra con un account nuovo torna in home: Shopify perde il
    // return_to. Il segno in sessione permette a initCheckoutAfterLogin di
    // riprendere il percorso verso il checkout.
    try { sessionStorage.setItem(CHECKOUT_AFTER_LOGIN_KEY, String(Date.now())); } catch (err) { /* navigazione privata */ }
    window.location.href = `${loginUrl.pathname}${loginUrl.search}`;
  }

  function initCheckoutAfterLogin() {
    const gate = document.querySelector("[data-fiscal-onboarding-gate]");
    if (!gate || gate.dataset.customerAuthenticated !== "true") return false;
    if (window.Shopify?.designMode) return false;

    let startedAt = null;
    try {
      startedAt = sessionStorage.getItem(CHECKOUT_AFTER_LOGIN_KEY);
      if (startedAt) sessionStorage.removeItem(CHECKOUT_AFTER_LOGIN_KEY);
    } catch (err) {
      return false;
    }
    if (!startedAt || Date.now() - Number(startedAt) > CHECKOUT_AFTER_LOGIN_MAX_AGE_MS) return false;
    // Il return_to e' stato rispettato: si e' gia' sull'onboarding.
    if (normalizePath(window.location.pathname) === getPath(gate.dataset.onboardingUrl)) return false;

    if (gate.dataset.onboardingComplete === "true") {
      goToDestination("/checkout");
    } else {
      goToRequiredOnboarding(gate.dataset);
    }
    return true;
  }

  function initCheckoutGate() {
    const gate = document.querySelector("[data-fiscal-onboarding-gate]");
    if (!gate) return;

    const onboardingIncomplete = gate.dataset.onboardingComplete !== "true";
    if (onboardingIncomplete) {
      document.body.classList.add("fiscal-onboarding-required");
    }

    function handleCheckoutIntent(event) {
      if (onboardingIncomplete) {
        event.preventDefault();
        event.stopPropagation();
        goToOnboarding(gate.dataset);
        return;
      }

      // Onboarding is already complete, so nothing would normally intercept this click —
      // but if a welcome discount is still pending (see email-login-nudge.js), re-apply
      // it right here, the last possible moment before checkout. The discount is applied
      // with a background request and the original click is then replayed, so the cart
      // form still submits (notes, attributes and edited quantities are not lost).
      const pendingDiscount = getCookie(DISCOUNT_COOKIE);
      if (!pendingDiscount) return;
      event.preventDefault();
      event.stopPropagation();
      deleteCookie(DISCOUNT_COOKIE);
      const trigger = event.type === "submit"
        ? event.submitter || event.target.querySelector('[name="checkout"], .bls-btn-checkout')
        : event.target.closest('[name="checkout"], .bls-btn-checkout');
      fetch(`/discount/${encodeURIComponent(pendingDiscount)}`, { credentials: "same-origin" })
        .catch(() => {})
        .then(() => {
          if (trigger) trigger.click();
          else goToDestination("/checkout");
        });
    }

    // "Accedi per acquistare" riporta qui con ?checkout=1: il cliente aveva gia'
    // chiesto il checkout, quindi lo si prosegue senza fargli ricliccare il
    // pulsante. Stessa strada del click: onboarding se i dati mancano, altrimenti
    // checkout (con l'eventuale sconto di benvenuto).
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "1") {
      params.delete("checkout");
      const query = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
      if (gate.dataset.customerAuthenticated === "true" && !window.Shopify?.designMode) {
        fetch("/cart.js", { credentials: "same-origin" })
          .then((response) => response.json())
          .then((cart) => {
            if (!cart.item_count) return;
            if (onboardingIncomplete) goToOnboarding(gate.dataset);
            else goToDestination("/checkout");
          })
          .catch(() => {});
      }
    }

    document.addEventListener(
      "click",
      (event) => {
        const checkoutTrigger = event.target.closest('[name="checkout"], .bls-btn-checkout');
        if (!checkoutTrigger) return;
        handleCheckoutIntent(event);
      },
      true
    );

    document.addEventListener(
      "submit",
      (event) => {
        const submitter = event.submitter;
        const isCheckoutSubmit = submitter
          ? submitter.getAttribute("name") === "checkout" || submitter.classList.contains("bls-btn-checkout")
          : event.target.querySelector('input[name="checkout"][type="hidden"]');
        if (!isCheckoutSubmit) return;
        handleCheckoutIntent(event);
      },
      true
    );
  }

  function setRequired(input, required) {
    if (!input) return;
    input.required = required;
    // I campi indirizzo non hanno data-field: senza il ripiego sulla classe
    // restavano obbligatori ma senza asterisco.
    input.closest("[data-field], .fiscal-onboarding__field")?.classList.toggle("is-required", required);
  }

  function normalizeVatNumber(value, countryCode) {
    const vatNumber = (value || "").toUpperCase().replace(/[\s.-]/g, "");

    if (countryCode === "IT") {
      return vatNumber.replace(/^IT/, "");
    }

    return vatNumber;
  }

  // Cifra di controllo della partita IVA italiana (Luhn sulle prime 10 cifre).
  // Le sole 11 cifre lasciavano passare un numero digitato male, e il VIES
  // italiano spesso non lo conosce comunque: si scopriva in fattura.
  function partitaIvaValida(numero) {
    if (!/^\d{11}$/.test(numero)) return false;
    let somma = 0;
    for (let i = 0; i < 10; i++) {
      let cifra = Number(numero.charAt(i));
      if (i % 2 === 1) {
        cifra *= 2;
        if (cifra > 9) cifra -= 9;
      }
      somma += cifra;
    }
    return (10 - (somma % 10)) % 10 === Number(numero.charAt(10));
  }

  // Formato del VAT ID per paese UE (senza il prefisso), come lo pubblica il
  // VIES. Prima bastavano 4 caratteri qualsiasi.
  const EU_VAT_FORMATS = {
    AT: /^U\d{8}$/, BE: /^[01]\d{9}$/, BG: /^\d{9,10}$/, CY: /^\d{8}[A-Z]$/,
    CZ: /^\d{8,10}$/, DE: /^\d{9}$/, DK: /^\d{8}$/, EE: /^\d{9}$/, GR: /^\d{9}$/,
    ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/, FI: /^\d{8}$/, FR: /^[A-Z0-9]{2}\d{9}$/,
    HR: /^\d{11}$/, HU: /^\d{8}$/, IE: /^(\d{7}[A-W][A-I]?|\d[A-Z+*]\d{5}[A-W])$/,
    LT: /^(\d{9}|\d{12})$/, LU: /^\d{8}$/, LV: /^\d{11}$/, MT: /^\d{8}$/,
    NL: /^\d{9}B\d{2}$/, PL: /^\d{10}$/, PT: /^\d{9}$/, RO: /^\d{2,10}$/,
    SE: /^\d{12}$/, SI: /^\d{8}$/, SK: /^\d{10}$/,
  };

  // Un esempio per paese, nella forma dei formati qui sopra (la Grecia usa EL).
  const EU_VAT_EXAMPLES = {
    AT: "ATU12345678", BE: "BE0123456789", BG: "BG123456789", CY: "CY12345678L",
    CZ: "CZ12345678", DE: "DE123456789", DK: "DK12345678", EE: "EE123456789",
    GR: "EL123456789", ES: "ESB12345678", FI: "FI12345678", FR: "FR12345678901",
    HR: "HR12345678901", HU: "HU12345678", IE: "IE1234567T", LT: "LT123456789",
    LU: "LU12345678", LV: "LV12345678901", MT: "MT12345678", NL: "NL123456789B01",
    PL: "PL1234567890", PT: "PT123456789", RO: "RO12345678", SE: "SE123456789001",
    SI: "SI12345678", SK: "SK1234567890",
  };

  function vatUeValido(numero, paese) {
    const prefisso = paese === "GR" ? "EL" : paese;
    let corpo = (numero || "").toUpperCase().replace(/[\s.-]/g, "");
    if (corpo.startsWith(prefisso)) corpo = corpo.slice(prefisso.length);
    const formato = EU_VAT_FORMATS[paese];
    return formato ? formato.test(corpo) : corpo.length >= 4;
  }

  function normalizeTaxCode(value) {
    return (value || "").toUpperCase().replace(/\s+/g, "");
  }

  // Il codice fiscale ha un carattere di controllo calcolabile: senza verificarlo
  // "16 caratteri alfanumerici" lascia passare qualsiasi cosa, e un codice
  // inventato si scopre solo quando lo SDI scarta la fattura.
  // Le posizioni numeriche accettano anche le lettere dell'omocodia (L M N P Q
  // R S T U V), che l'Agenzia delle Entrate usa quando due persone otterrebbero
  // lo stesso codice: quei codici sono validi.
  var TAX_CODE_RE = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;
  var TAX_CODE_ODD = {
    0:1,1:0,2:5,3:7,4:9,5:13,6:15,7:17,8:19,9:21,
    A:1,B:0,C:5,D:7,E:9,F:13,G:15,H:17,I:19,J:21,K:2,L:4,M:18,N:20,O:11,P:3,
    Q:6,R:8,S:12,T:14,U:16,V:10,W:22,X:25,Y:24,Z:23
  };
  var TAX_CODE_EVEN = {
    0:0,1:1,2:2,3:3,4:4,5:5,6:6,7:7,8:8,9:9,
    A:0,B:1,C:2,D:3,E:4,F:5,G:6,H:7,I:8,J:9,K:10,L:11,M:12,N:13,O:14,P:15,
    Q:16,R:17,S:18,T:19,U:20,V:21,W:22,X:23,Y:24,Z:25
  };

  // Ritorna null se il codice va bene, altrimenti quale controllo ha fallito:
  // "length", "format" o "checksum". Dire soltanto "codice non valido" lascia
  // il cliente a indovinare, e chi sbaglia una lettera di solito ricontrolla
  // tutto tranne quella.
  function taxCodeProblem(code) {
    if (code.length !== 16) return "length";
    if (!TAX_CODE_RE.test(code)) return "format";
    var somma = 0;
    for (var i = 0; i < 15; i++) {
      var c = code.charAt(i);
      somma += i % 2 === 0 ? TAX_CODE_ODD[c] : TAX_CODE_EVEN[c];
    }
    if (code.charAt(15) !== "ABCDEFGHIJKLMNOPQRSTUVWXYZ".charAt(somma % 26)) {
      return "checksum";
    }
    return null;
  }

  function initOnboardingForm() {
    const form = document.querySelector("[data-fiscal-onboarding-form]");
    if (!form) return;

    const fields = {
      country: form.querySelector('[name="country_code"]'),
      type: form.querySelector('[name="customer_type"]'),
      firstName: form.querySelector('[name="first_name"]'),
      lastName: form.querySelector('[name="last_name"]'),
      companyName: form.querySelector('[name="company_name"]'),
      vatNumber: form.querySelector('[name="vat_number"]'),
      taxCode: form.querySelector('[name="tax_code"]'),
      companyTaxCode: form.querySelector('[name="company_tax_code"]'),
      companyTaxCodeDiffers: form.querySelector('[name="company_tax_code_differs"]'),
      sdiCode: form.querySelector('[name="sdi_code"]'),
      billingSameAsShipping: form.querySelector('[name="billing_same_as_shipping"]'),
      newsletterOptin: form.querySelector('[name="newsletter_optin"]'),
      shipAddress1: form.querySelector('[name="shipping_address1"]'),
      shipAddress2: form.querySelector('[name="shipping_address2"]'),
      shipZip: form.querySelector('[name="shipping_zip"]'),
      shipCity: form.querySelector('[name="shipping_city"]'),
      shipProvince: form.querySelector('[name="shipping_province"]'),
      shipProvinceText: form.querySelector('[name="shipping_province_text"]'),
      shipCountry: form.querySelector('[name="shipping_country_code"]'),
      shipPhone: form.querySelector('[name="shipping_phone"]'),
      shipPhonePrefix: form.querySelector('[name="shipping_phone_prefix"]'),
      billAddress1: form.querySelector('[name="billing_address1"]'),
      billAddress2: form.querySelector('[name="billing_address2"]'),
      billZip: form.querySelector('[name="billing_zip"]'),
      billCity: form.querySelector('[name="billing_city"]'),
      billProvince: form.querySelector('[name="billing_province"]'),
      billProvinceText: form.querySelector('[name="billing_province_text"]'),
      billCountry: form.querySelector('[name="billing_country_code"]'),
      billPhone: form.querySelector('[name="billing_phone"]'),
      billPhonePrefix: form.querySelector('[name="billing_phone_prefix"]'),
    };
    // Shopify restituisce il telefono salvato gia' completo ("+393331234567"):
    // nel campo accanto al prefisso "+39" il cliente lo vedeva due volte. Si
    // separa all'apertura; composePhone lo ricompone uguale.
    function separaTelefonoSalvato(prefixSelect, numberInput) {
      const numero = (numberInput?.value || "").replace(/\s+/g, "");
      if (!prefixSelect || !numero.startsWith("+")) return;
      const opzioni = [...prefixSelect.options]
        .filter((o) => o.value && numero.startsWith(o.value.replace(/\s+/g, "")))
        .sort((x, y) => y.value.length - x.value.length);
      if (!opzioni.length) return;
      const scelta = opzioni.find((o) => o.selected) || opzioni[0];
      prefixSelect.value = scelta.value;
      numberInput.value = numero.slice(scelta.value.replace(/\s+/g, "").length);
    }
    separaTelefonoSalvato(fields.shipPhonePrefix, fields.shipPhone);
    separaTelefonoSalvato(fields.billPhonePrefix, fields.billPhone);

    const billingWrapper = form.querySelector("[data-billing-fields]");
    const shippingToggle = form.querySelector("[data-shipping-toggle]");
    const shippingCard = form.querySelector("[data-shipping-card]");
    const shippingTitle = form.querySelector("[data-shipping-title]");
    const shippingWhy = form.querySelector("[data-shipping-why]");
    const toggleLabel = form.querySelector("[data-billing-toggle-label]");
    const billingWhy = form.querySelector("[data-billing-why]");
    const taxCodeHint = form.querySelector("[data-tax-code-hint]");
    const soleTraderHint = form.querySelector("[data-sole-trader-hint]");
    const grid = form.querySelector(".fiscal-onboarding__grid");
    const message = form.querySelector("[data-fiscal-onboarding-message]");
    const loader = document.querySelector("[data-fiscal-onboarding-loader]");
    const submitButton = form.querySelector('[type="submit"]');
    const vatHelp = form.querySelector("[data-vat-help]");
    const vatLookupButton = form.querySelector("[data-vat-lookup-trigger]");
    const viesModal = document.querySelector("[data-vies-modal]");
    const fiscalBanner = document.querySelector("[data-fiscal-banner]");
    const progressSteps = document.querySelectorAll("[data-progress-step]");
    let fiscalBannerWasVisible = false;
    let modalReturnFocus = null;
    publishStartOnFirstInput(form, () => ({ onboarding_form: "completo", entry_point: getEntryPoint() }));

    function setProgress(step) {
      progressSteps.forEach((item) => {
        const itemStep = Number(item.dataset.progressStep);
        item.classList.toggle("is-active", itemStep === step);
        item.classList.toggle("is-complete", itemStep < step);
        if (itemStep === step) item.setAttribute("aria-current", "step");
        else item.removeAttribute("aria-current");
      });
    }

    function proceed() {
      // Letto prima di cancellarlo: e' il ripiego di chi entra senza return_to
      // (la fascia), che altrimenti finirebbe sempre su /account.
      const destination = getReturnTo(form.dataset.successRedirect);
      try { sessionStorage.removeItem(ONBOARDING_RETURN_KEY); } catch (error) {}
      goToDestination(destination);
    }

    function fillContactBody() {
      const bodyField = viesModal?.querySelector("[data-vies-contact-body]");
      if (!bodyField || bodyField.value.trim()) return;
      bodyField.value = [
        "Richiesta di verifica manuale della partita IVA (VIES non raggiungibile al momento dell'onboarding).",
        `Ragione sociale: ${fields.companyName?.value.trim() || "-"}`,
        `Partita IVA: ${normalizeVatNumber(fields.vatNumber?.value, fields.country?.value) || "-"}`,
        `Paese: ${fields.country?.value || "-"}`,
        // La notifica Shopify in italiano lascia "%{creation_date}" al posto
        // della data e non si puo' modificare: la data viaggia nel corpo.
        `Data richiesta: ${new Date().toLocaleString("it-IT", { timeZone: "Europe/Rome" })}`,
      ].join("\n");
    }

    function openViesModal(variant) {
      if (!viesModal) {
        proceed();
        return;
      }
      viesModal.querySelectorAll("[data-vies-variant]").forEach((el) => {
        el.hidden = el.dataset.viesVariant !== variant;
      });
      if (variant === "unavailable") fillContactBody();
      fiscalBannerWasVisible = Boolean(fiscalBanner && !fiscalBanner.hidden);
      if (fiscalBanner) fiscalBanner.hidden = true;
      const dialog = viesModal.querySelector('[role="dialog"]');
      modalReturnFocus = document.activeElement;
      dialog?.setAttribute(
        "aria-labelledby",
        variant === "invalid" ? "ViesModalInvalidTitle" : "ViesModalUnavailableTitle"
      );
      viesModal.hidden = false;
      requestAnimationFrame(() => {
        const firstControl = dialog?.querySelector("button, input, textarea, select, [href]");
        (firstControl || dialog)?.focus();
      });
    }

    function closeViesModal() {
      if (viesModal) viesModal.hidden = true;
      if (fiscalBanner && fiscalBannerWasVisible) fiscalBanner.hidden = false;
      modalReturnFocus?.focus?.();
      modalReturnFocus = null;
    }

    function showMessage(text, type) {
      if (!message) return;
      message.textContent = text || "";
      message.className = `fiscal-onboarding__message ${type ? `is-${type}` : ""}`;
      message.hidden = !text;
    }

    // L'errore va dove sta il campo, non solo in fondo al modulo: il campo
    // sbagliato era spesso fuori dallo schermo, e il cliente rileggeva tutto.
    function clearFieldError(input) {
      const contenitore = input?.closest(".fiscal-onboarding__field");
      if (!contenitore) return;
      contenitore.classList.remove("is-invalid");
      input.removeAttribute("aria-invalid");
      contenitore.querySelector("[data-field-error]")?.remove();
    }

    function showFieldError(input, text, { scroll = true } = {}) {
      form.querySelectorAll(".fiscal-onboarding__field.is-invalid").forEach((campo) => {
        const errato = campo.querySelector("[aria-invalid]");
        if (errato) clearFieldError(errato);
        else campo.classList.remove("is-invalid");
      });
      const contenitore = input?.closest(".fiscal-onboarding__field");
      if (!contenitore || input.disabled) return;
      contenitore.classList.add("is-invalid");
      input.setAttribute("aria-invalid", "true");
      const nota = document.createElement("small");
      nota.className = "fiscal-onboarding__field-error";
      nota.setAttribute("data-field-error", "");
      nota.textContent = text;
      contenitore.appendChild(nota);
      if (scroll) {
        contenitore.scrollIntoView({ block: "center", behavior: "smooth" });
        input.focus({ preventScroll: true });
      }
    }

    function isItaly() {
      return fields.country?.value === "IT";
    }

    function isBusiness() {
      return fields.type?.value === "B2B" || fields.type?.value === "B2B_PERSON";
    }

    function isPersonBusiness() {
      return fields.type?.value === "B2B_PERSON";
    }

    function isCompany() {
      return fields.type?.value === "B2B";
    }

    function getCustomerTypeLabel() {
      if (isPersonBusiness()) return "Persona fisica con P.IVA";
      if (isCompany()) return "Azienda";
      return "Privato";
    }

    // La spunta mostrata all'utente e' "il codice fiscale e' diverso dalla partita
    // IVA", non spuntata di default perche' nella grande maggioranza delle aziende
    // i due coincidono. Tutto il resto del modulo continua a ragionare in termini
    // di "same as vat", che e' anche cio' che viene salvato: l'inversione avviene
    // solo qui, per non spargere doppie negazioni nel codice.
    function isCompanyTaxCodeSameAsVat() {
      return !fields.companyTaxCodeDiffers?.checked;
    }

    function syncCompanyTaxCode() {
      if (!fields.companyTaxCode || !fields.companyTaxCodeDiffers) return;

      const sameAsVat = isCompany() && isCompanyTaxCodeSameAsVat();
      const riga = fields.companyTaxCode.closest("[data-field]");
      if (riga) riga.hidden = !isCompany() || sameAsVat;
      fields.companyTaxCode.disabled = sameAsVat || fields.companyTaxCode.closest("[data-field]")?.hidden;

      if (sameAsVat) {
        fields.companyTaxCode.value = normalizeVatNumber(fields.vatNumber.value, fields.country.value);
      }
    }

    // Le prime sei lettere del codice fiscale vengono da cognome e nome. Se non
    // tornano, quasi sempre e' il codice di un familiare copiato per sbaglio:
    // lo diciamo senza bloccare (nomi stranieri, doppi cognomi, omonimie).
    function lettereCodice(testo, nome) {
      const pulito = (testo || "")
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .toUpperCase().replace(/[^A-Z]/g, "");
      const consonanti = pulito.replace(/[AEIOU]/g, "");
      const vocali = pulito.replace(/[^AEIOU]/g, "");
      if (nome && consonanti.length >= 4) {
        return consonanti.charAt(0) + consonanti.charAt(2) + consonanti.charAt(3);
      }
      return (consonanti + vocali + "XXX").slice(0, 3);
    }

    function controllaNominativoCodiceFiscale() {
      if (!taxCodeHint) return;
      const codice = normalizeTaxCode(fields.taxCode?.value);
      const daControllare =
        isItaly() && (!isBusiness() || isPersonBusiness()) &&
        codice && !taxCodeProblem(codice) &&
        fields.firstName?.value.trim() && fields.lastName?.value.trim();
      const atteso = daControllare
        ? lettereCodice(fields.lastName.value, false) + lettereCodice(fields.firstName.value, true)
        : "";
      const diverso = daControllare && codice.slice(0, 6) !== atteso;
      taxCodeHint.textContent = diverso ? form.dataset.messageTaxCodeNameMismatch || "" : "";
      taxCodeHint.hidden = !diverso;
    }

    // Una ditta individuale registrata come azienda finisce in Radix con la
    // P.IVA al posto del CF personale, e lo SdI scarta la fattura (901165).
    // La ragione sociale lo tradisce: "... DI NOME COGNOME", o nome e cognome
    // dell'account, senza forma societaria. Solo un avviso: i falsi positivi
    // (societa' con il nome del socio, "KG des ...") devono poter proseguire.
    // Sulle parole senza punti: "S.r.l." diventa "SRL".
    const FORMA_SOCIETARIA = / (SRLS?|SPA|SAS|SNC|SS|SAPA|SCARL|SOC|SOCIETA|COOP\w*|CONSORZIO|ONLUS|ASSOCIAZIONE|FONDAZIONE|KG|GMBH|OHG|& C) /;

    function controllaDittaIndividuale() {
      if (!soleTraderHint) return;
      const testo = (fields.companyName?.value || "")
        .normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().trim();
      const parole = " " + testo.replace(/\./g, "").replace(/[^A-Z0-9&]+/g, " ") + " ";
      const nome = (fields.firstName?.value || "").trim().toUpperCase();
      const cognome = (fields.lastName?.value || "").trim().toUpperCase();
      const sospetta =
        isItaly() && isCompany() && testo && !FORMA_SOCIETARIA.test(parole) &&
        (/\bDI\s+[A-Z']+\s+[A-Z']+$/.test(testo) ||
          (nome && cognome && testo.includes(nome) && testo.includes(cognome)));
      soleTraderHint.textContent = sospetta ? form.dataset.messageSoleTraderWarning || "" : "";
      soleTraderHint.hidden = !sospetta;
    }

    function isBillingSameAsShipping() {
      return Boolean(fields.billingSameAsShipping?.checked);
    }

    // Chi dei due indirizzi comanda, caso per caso.
    //
    // Privato: l'indirizzo di spedizione e, se diversa, la residenza a cui
    //   intestare la fattura (deve essere esatta anche per lui). Stessa
    //   disposizione dell'azienda estera: spedizione prima e la vecchia spunta.
    // Azienda italiana: la sede la conosciamo dal VIES, quindi viene per prima e
    //   la spunta dice "spedisco qui"; togliendola si scrive dove va la merce.
    // Azienda estera: la sede non la sappiamo (Germania e Spagna non la
    //   pubblicano), quindi resta la spedizione per prima e la vecchia spunta.
    //
    // La spunta cambia riquadro insieme al verso: "questo indirizzo" deve
    // riferirsi a quello sotto cui sta scritto, altrimenti non si capisce quale
    // dei due intenda.
    function spedisceAllaSede() {
      return isBusiness() && isItaly() && isBillingSameAsShipping();
    }

    function syncAddressLayout() {
      const business = isBusiness();
      const sedeNota = business && isItaly();
      const coincidono = isBillingSameAsShipping();
      const aperto = campiVisibili();
      // Un indirizzo solo (privato con fatturazione uguale): il titolo dice che
      // vale per consegna e fattura.
      const indirizzoUnico = !business && coincidono;

      if (shippingTitle) {
        shippingTitle.textContent = indirizzoUnico
          ? form.dataset.messageAddressSingleHeading || shippingTitle.textContent
          : form.dataset.messageAddressShippingHeading || shippingTitle.textContent;
      }
      if (shippingWhy) {
        shippingWhy.textContent = indirizzoUnico
          ? form.dataset.messageAddressSingleWhy || shippingWhy.textContent
          : form.dataset.messageAddressShippingWhy || shippingWhy.textContent;
      }

      if (shippingToggle) {
        const casa = sedeNota ? billingWrapper : shippingCard;
        if (casa && shippingToggle.parentElement !== casa) casa.appendChild(shippingToggle);
        shippingToggle.hidden = false;
      }
      if (toggleLabel) {
        toggleLabel.textContent = sedeNota
          ? form.dataset.messageAddressShipHere || toggleLabel.textContent
          : form.dataset.messageAddressBillingSame || toggleLabel.textContent;
      }
      // Il riquadro di fatturazione dice a cosa serve, come quello di consegna:
      // per un privato e' la residenza, per un'azienda la sede legale.
      if (billingWhy) {
        billingWhy.textContent = business
          ? form.dataset.messageAddressBillingWhyBusiness || billingWhy.textContent
          : form.dataset.messageAddressBillingWhyPrivate || billingWhy.textContent;
      }

      grid?.classList.toggle("is-billing-first", sedeNota);

      // Quando i due coincidono sparisce quello che si puo' ricavare dall'altro:
      // con la sede nota sparisce la spedizione, altrimenti la fatturazione.
      const mostraSpedizione = aperto && !(sedeNota && coincidono);
      const mostraFatturazione = aperto && !(!sedeNota && coincidono);

      if (shippingCard) shippingCard.hidden = !mostraSpedizione;
      if (billingWrapper) billingWrapper.hidden = !mostraFatturazione;

      shippingCard?.querySelectorAll("input, select").forEach((input) => {
        input.disabled = !mostraSpedizione;
      });
      billingWrapper?.querySelectorAll("input, select").forEach((input) => {
        input.disabled = !mostraFatturazione;
      });
      // La spunta vive dentro un riquadro ma non ne segue la sorte: e' lei a
      // decidere quale dei due si vede.
      if (fields.billingSameAsShipping) fields.billingSameAsShipping.disabled = !aperto;

      setRequired(fields.shipAddress1, mostraSpedizione);
      setRequired(fields.shipZip, mostraSpedizione);
      setRequired(fields.shipCity, mostraSpedizione);
      setRequired(fields.shipCountry, mostraSpedizione);
      setRequired(fields.shipPhone, mostraSpedizione);
      setRequired(fields.billAddress1, mostraFatturazione);
      setRequired(fields.billZip, mostraFatturazione);
      setRequired(fields.billCity, mostraFatturazione);
      setRequired(fields.billCountry, mostraFatturazione);
      // Il telefono serve al corriere: sulla fatturazione e' facoltativo, salvo
      // quando la merce va alla sede e il riquadro di fatturazione e' l'unico.
      const telefonoSede = mostraFatturazione && spedisceAllaSede();
      setRequired(fields.billPhone, telefonoSede);
      const etichettaTelefono = form.querySelector('label[for="BillPhone"]');
      if (etichettaTelefono) {
        etichettaTelefono.textContent = telefonoSede
          ? form.dataset.messagePhoneLabel || etichettaTelefono.textContent
          : form.dataset.messagePhoneOptional || etichettaTelefono.textContent;
      }

      syncProvinceControls(fields.billCountry, fields.billProvince, fields.billProvinceText, !mostraFatturazione);
    }

    // Quando si spedisce alla sede, i campi spedizione sono nascosti ma il PIM li
    // vuole comunque pieni: si copiano dalla fatturazione prima di validare, cosi'
    // il payload resta quello di sempre e billing_same_as_shipping continua a
    // significare "i due indirizzi coincidono".
    function copiaSedeNellaSpedizione() {
      if (!spedisceAllaSede()) return;
      [
        [fields.billAddress1, fields.shipAddress1],
        [fields.billAddress2, fields.shipAddress2],
        [fields.billZip, fields.shipZip],
        [fields.billCity, fields.shipCity],
        [fields.billCountry, fields.shipCountry],
        [fields.billProvince, fields.shipProvince],
        [fields.billProvinceText, fields.shipProvinceText],
        [fields.billPhone, fields.shipPhone],
        [fields.billPhonePrefix, fields.shipPhonePrefix],
      ].forEach(([da, a]) => {
        if (da && a) a.value = da.value;
      });
    }

    // La spunta "fattura allo stesso indirizzo" parte selezionata per il
    // privato e per l'azienda italiana (la sede arriva dal VIES), vuota per
    // l'azienda estera: il VIES non ci da' la sede, e con la spunta la fattura
    // veniva intestata al magazzino o al negozio di consegna senza che nessuno
    // lo notasse. Decide solo finche' la spunta non l'ha scelta il cliente, o
    // la fatturazione salvata, o il confronto con la sede VIES.
    let spuntaFatturazioneDecisa = false;

    function applicaSpuntaFatturazione() {
      if (spuntaFatturazioneDecisa || !fields.billingSameAsShipping) return;
      fields.billingSameAsShipping.checked = !(isBusiness() && !isItaly());
    }

    function syncFields() {
      const italy = isItaly();
      // La ditta individuale vale anche fuori dall'Italia: prima qui la si
      // nascondeva e la si cambiava in "Societa'" senza dirlo, mentre il
      // riquadro prezzi la offriva (cliente 901168, Belgio). Il PIM la registra
      // come persona fisica con inversione contabile.
      const business = isBusiness();
      const company = isCompany();
      const personBusiness = isPersonBusiness();

      const campiAperti = campiVisibili();
      const attesaCampi = form.querySelector("[data-vat-gate-hint]");
      if (attesaCampi) attesaCampi.hidden = campiAperti;

      form.querySelectorAll("[data-visible-for]").forEach((field) => {
        const visibility = field.dataset.visibleFor;
        const visible =
          visibility === "all" ||
          (visibility === "b2c" && !business) ||
          (visibility === "b2b" && business) ||
          (visibility === "business" && business) ||
          (visibility === "company" && company) ||
          (visibility === "it-b2c" && italy && !business) ||
          (visibility === "it-b2b" && italy && business) ||
          (visibility === "it-business" && italy && business) ||
          (visibility === "it-company" && italy && company) ||
          (visibility === "it-person" && italy && personBusiness) ||
          (visibility === "it-tax-code" && italy && (!business || personBusiness));

        const mostrato = visible && (campiAperti || !("vatGate" in field.dataset));
        field.hidden = !mostrato;
        field.querySelectorAll("input, select").forEach((input) => {
          input.disabled = !mostrato;
          if (!mostrato) {
            input.removeAttribute("required");
          }
        });
      });

      setRequired(fields.firstName, true);
      setRequired(fields.lastName, true);
      // La partita IVA e' l'unica a restare obbligatoria a cancello chiuso: e'
      // lei ad aprirlo. Tutte le altre lo diventano quando si vedono.
      setRequired(fields.companyName, company && campiAperti);
      setRequired(fields.vatNumber, business);
      setRequired(fields.taxCode, italy && (!business || personBusiness) && campiAperti);
      setRequired(fields.companyTaxCode,
        italy && company && !isCompanyTaxCodeSameAsVat() && campiAperti);
      setRequired(fields.sdiCode, italy && business && campiAperti);
      syncProvinceControls(fields.shipCountry, fields.shipProvince, fields.shipProvinceText, false);
      syncCompanyTaxCode();
      controllaDittaIndividuale();
      applicaSpuntaFatturazione();
      syncAddressLayout();

      if (vatHelp) {
        // L'esempio e' quello del paese scelto: a un cliente tedesco un numero
        // francese faceva pensare di aver sbagliato paese.
        vatHelp.textContent = italy
          ? form.dataset.messageVatHelpIt
          : (form.dataset.messageVatHelpEu || "").replace("__EXAMPLE__", EU_VAT_EXAMPLES[fields.country?.value] || "");
      }
    }

    function composePhone(prefixSelect, numberInput) {
      const number = (numberInput?.value || "").trim();
      if (!number) return "";
      // If the user typed a full international number, keep it as-is.
      if (number.startsWith("+")) return number.replace(/\s+/g, " ");
      const prefix = (prefixSelect?.value || "").trim();
      return prefix ? `${prefix} ${number}` : number;
    }

    // Italy: show the province select (official code, e.g. "Brescia (BS)");
    // other countries: free-text region field. Only the visible control is enabled
    // so the payload always reads from the right source.
    function syncProvinceControls(countrySelect, provinceSelect, provinceText, forceDisabled) {
      if (!provinceSelect || !provinceText) return;
      const italy = countrySelect?.value === "IT";
      provinceSelect.hidden = !italy;
      provinceSelect.disabled = forceDisabled || !italy;
      provinceText.hidden = italy;
      provinceText.disabled = forceDisabled || italy;
      setRequired(provinceSelect, !forceDisabled && italy);
      const riquadro = provinceSelect.closest(".fiscal-onboarding__address, .fiscal-onboarding__billing");
      const etichetta = riquadro?.querySelector("[data-province-label]");
      if (etichetta) {
        etichetta.textContent = italy
          ? form.dataset.messageProvinceLabel || etichetta.textContent
          : form.dataset.messageProvinceOptional || etichetta.textContent;
      }
      // In Italia il CAP e' fatto di sole cifre: tastiera numerica sul telefono.
      // Non altrove: "1234 AB" (Paesi Bassi) o "D-01326" hanno lettere.
      const cap = riquadro?.querySelector('input[name$="_zip"]');
      if (cap) cap.inputMode = italy ? "numeric" : "text";
    }

    function getProvincePayload(countrySelect, provinceSelect, provinceText) {
      if (countrySelect?.value === "IT") {
        const option = provinceSelect?.selectedOptions?.[0];
        return {
          province: option?.dataset.name || "",
          province_code: provinceSelect?.value || "",
        };
      }
      return { province: (provinceText?.value || "").trim(), province_code: "" };
    }

    function syncPhonePrefix(prefixSelect, countrySelect) {
      if (!prefixSelect || !countrySelect || prefixSelect.dataset.userSet === "true") return;
      const option = prefixSelect.querySelector(`option[data-country="${countrySelect.value}"]`);
      if (option) prefixSelect.value = option.value;
    }

    // Il campo dell'ultimo errore trovato da validate(): submit lo evidenzia.
    let campoErrato = null;

    function fail(campo, testo) {
      campoErrato = campo || null;
      return testo || form.dataset.messageValidationFailed;
    }

    function telefonoValido(input) {
      return (input?.value || "").replace(/\D/g, "").length >= 6;
    }

    // Controlli di un riquadro indirizzo, nell'ordine in cui si vedono i campi.
    function problemaIndirizzo(campi, telefonoObbligatorio) {
      const italia = campi.country?.value === "IT";
      const d = form.dataset;
      if (!campi.address1?.value.trim()) return fail(campi.address1, d.messageAddressIncomplete);
      const cap = (campi.zip?.value || "").trim();
      if (!cap) return fail(campi.zip, d.messageAddressIncomplete);
      if (italia && !/^\d{5}$/.test(cap)) return fail(campi.zip, d.messageZipInvalid);
      const citta = (campi.city?.value || "").trim();
      if (!citta) return fail(campi.city, d.messageAddressIncomplete);
      if (!/\p{L}/u.test(citta)) return fail(campi.city, d.messageCityInvalid);
      if (italia && !campi.province?.value.trim()) return fail(campi.province, d.messageAddressIncomplete);
      if (!campi.country?.value) return fail(campi.country, d.messageAddressIncomplete);
      const telefono = (campi.phone?.value || "").trim();
      if (telefonoObbligatorio && !telefono) return fail(campi.phone, d.messageAddressIncomplete);
      if (telefono && !telefonoValido(campi.phone)) return fail(campi.phone, d.messagePhoneInvalid);
      return "";
    }

    function validate() {
      campoErrato = null;
      const d = form.dataset;
      const country = fields.country?.value;
      const business = isBusiness();
      const company = isCompany();
      const personBusiness = isPersonBusiness();
      const italy = country === "IT";
      const taxCode = normalizeTaxCode(fields.taxCode?.value);
      const vatNumber = normalizeVatNumber(fields.vatNumber?.value, country);
      const companyTaxCode = normalizeTaxCode(fields.companyTaxCode?.value);

      // Spazi soltanto non sono un nome: passavano il browser e li fermava il
      // server, con un messaggio solo in italiano.
      if (!fields.firstName?.value.trim()) return fail(fields.firstName, d.messageFirstNameRequired);
      if (!fields.lastName?.value.trim()) return fail(fields.lastName, d.messageLastNameRequired);

      if (italy && business) {
        if (!/^\d{11}$/.test(vatNumber)) return fail(fields.vatNumber, d.messageVatItInvalid);
        if (!partitaIvaValida(vatNumber)) return fail(fields.vatNumber, d.messageVatItChecksum);
      }
      if (business && !italy && !vatUeValido(vatNumber, country)) {
        return fail(fields.vatNumber, d.messageVatEuInvalid);
      }

      if (company && !fields.companyName?.value.trim()) {
        return fail(fields.companyName, d.messageCompanyNameRequired);
      }

      if (italy && (!business || personBusiness)) {
        var problema = taxCodeProblem(taxCode);
        if (problema) {
          return fail(
            fields.taxCode,
            {
              length: d.messageTaxCodeLength,
              format: d.messageTaxCodeFormat,
              checksum: d.messageTaxCodeChecksum,
            }[problema] || d.messageTaxCodeInvalid
          );
        }
      }

      // Stessa regola del PIM: un'azienda ha un CF di 11 cifre. Uno di 16
      // caratteri e' di una persona, quindi di una ditta individuale, che va
      // registrata come persona fisica con P.IVA (#73, cliente 901165).
      if (italy && company && !isCompanyTaxCodeSameAsVat() && !/^\d{11}$/.test(companyTaxCode)) {
        return fail(
          fields.companyTaxCode,
          taxCodeProblem(companyTaxCode)
            ? d.messageCompanyTaxCodeInvalid
            : d.messageCompanyTaxCodePersonal
        );
      }

      if (italy && business) {
        const sdi = (fields.sdiCode?.value || "").trim().toUpperCase();
        if (!/^[A-Z0-9]{7}$/.test(sdi)) return fail(fields.sdiCode, d.messageSdiInvalid);
      }

      // L'azienda italiana compila prima la sede (fatturazione); gli altri
      // prima la consegna. Si controlla nello stesso ordine in cui si vedono.
      const spedizione = {
        address1: fields.shipAddress1, zip: fields.shipZip, city: fields.shipCity,
        province: fields.shipProvince, country: fields.shipCountry, phone: fields.shipPhone,
      };
      const fatturazione = {
        address1: fields.billAddress1, zip: fields.billZip, city: fields.billCity,
        province: fields.billProvince, country: fields.billCountry, phone: fields.billPhone,
      };
      // Con la spedizione alla sede il telefono del riquadro di fatturazione
      // serve anche al corriere: e' copiato nella spedizione e li' si controlla.
      const sedePrima = business && italy;
      const controlli = [[spedizione, true]];
      if (!isBillingSameAsShipping()) {
        if (sedePrima) controlli.unshift([fatturazione, false]);
        else controlli.push([fatturazione, false]);
      }
      for (const [campi, telefonoObbligatorio] of controlli) {
        // I campi nascosti (spedizione copiata dalla sede) non si evidenziano:
        // l'errore va sul riquadro che il cliente vede.
        const visibili = !campi.address1?.closest("[hidden]");
        const problemaTrovato = problemaIndirizzo(campi, telefonoObbligatorio);
        if (problemaTrovato) {
          if (!visibili && spedisceAllaSede()) {
            const corrispondente = Object.keys(campi).find((k) => campi[k] === campoErrato);
            campoErrato = corrispondente ? fatturazione[corrispondente] : fields.billAddress1;
          }
          return problemaTrovato;
        }
      }

      return "";
    }

    function collectEndpointMessages(value) {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value.flatMap(collectEndpointMessages);
      }
      if (typeof value === "object") {
        return Object.values(value).flatMap(collectEndpointMessages);
      }
      return [String(value)];
    }

    // Il PIM manda un codice per ogni errore: lo traduciamo con i testi del tema.
    // I codici degli indirizzi hanno il prefisso shipping_/billing_; quelli dei
    // campi mancanti (…_required) diventano "indirizzo incompleto".
    let traduzioniCodici = null;
    function messaggioPerCodice(codice) {
      if (traduzioniCodici === null) {
        try {
          traduzioniCodici = JSON.parse(form.dataset.errorCodes || "{}");
        } catch (err) {
          traduzioniCodici = {};
        }
      }
      const senzaPrefisso = String(codice).replace(/^(shipping|billing)_/, "");
      return (
        traduzioniCodici[codice] ||
        traduzioniCodici[senzaPrefisso] ||
        (senzaPrefisso.endsWith("_required") && /^(shipping|billing)_/.test(codice)
          ? traduzioniCodici.address_incomplete
          : "")
      );
    }

    function getEndpointErrorMessage(result) {
      if (!result || typeof result !== "object") {
        return form.dataset.messageValidationFailed;
      }

      if (Array.isArray(result.error_codes) && result.error_codes.length) {
        const tradotti = [...new Set(result.error_codes.map(messaggioPerCodice))];
        // Solo se li conosciamo tutti: un codice nuovo del PIM non deve far
        // sparire il suo messaggio.
        if (tradotti.every(Boolean)) return tradotti.join(" ");
      }

      const messages = [
        ...collectEndpointMessages(result.errors),
        ...collectEndpointMessages(result.write_errors),
        ...collectEndpointMessages(result.non_field_errors),
        ...collectEndpointMessages(result.detail_errors),
      ];
      if (messages.length > 0) {
        return messages.join(" ");
      }

      if (result.message) return result.message;
      if (result.error) return result.error;
      if (result.detail) return result.detail;
      if (result.status === "invalid") return form.dataset.messageFiscalInvalid;
      if (result.status === "write_error") return form.dataset.messageWriteError;

      return form.dataset.messageValidationFailed;
    }

    async function submit(event) {
      event.preventDefault();
      setProgress(3);
      showMessage("", "");

      // Il click su "Salva" ha appena fatto uscire dal campo partita IVA: se la
      // ricerca e' partita, si valida dopo che ha finito, non contro di lei.
      if (lookupInCorso) {
        loader?.removeAttribute("hidden");
        try {
          await lookupInCorso;
        } finally {
          loader?.setAttribute("hidden", "");
        }
      }
      // Ultima rete: chi arriva a premere Salva deve vedere i campi che gli
      // stiamo per contestare, anche se non e' mai passato dalla partita IVA.
      sbloccaCampi();
      copiaSedeNellaSpedizione();

      const validationError = validate();
      if (validationError) {
        showMessage(validationError, "error");
        if (campoErrato) showFieldError(campoErrato, validationError);
        setProgress(2);
        return;
      }

      const countryCode = fields.country.value;
      const business = isBusiness();
      const company = isCompany();
      const personBusiness = isPersonBusiness();
      const italy = countryCode === "IT";
      const customerTypeLabel = getCustomerTypeLabel();
      const payloadCustomerType = personBusiness ? "B2B_PERSON" : company ? "B2B" : "B2C";
      const vatNumber = normalizeVatNumber(fields.vatNumber.value, countryCode);
      const taxCode = normalizeTaxCode(fields.taxCode.value);
      const companyName = fields.companyName.value.trim();
      const sdiCode = fields.sdiCode.value.trim();
      const companyTaxCodeSameAsVat = italy && company && isCompanyTaxCodeSameAsVat();
      const companyTaxCodeSameAsVatValue = company ? String(companyTaxCodeSameAsVat) : "";
      const companyTaxCode = companyTaxCodeSameAsVat
        ? vatNumber
        : normalizeTaxCode(fields.companyTaxCode?.value);

      if (business) {
        fields.vatNumber.value = vatNumber;
      }
      if (italy && (!business || personBusiness)) {
        fields.taxCode.value = taxCode;
      }
      if (italy && company && fields.companyTaxCode) {
        fields.companyTaxCode.value = companyTaxCode;
      }

      const recipientFirst = fields.firstName.value.trim();
      const recipientLast = fields.lastName.value.trim();
      const addressCompany = company ? companyName : "";

      const shipProvince = getProvincePayload(fields.shipCountry, fields.shipProvince, fields.shipProvinceText);
      const shippingAddress = {
        first_name: recipientFirst,
        last_name: recipientLast,
        company: addressCompany,
        address1: fields.shipAddress1.value.trim(),
        address2: fields.shipAddress2.value.trim(),
        zip: fields.shipZip.value.trim(),
        city: fields.shipCity.value.trim(),
        province: shipProvince.province,
        province_code: shipProvince.province_code,
        country_code: fields.shipCountry.value,
        phone: composePhone(fields.shipPhonePrefix, fields.shipPhone),
      };

      const billingSame = isBillingSameAsShipping();
      const billProvince = billingSame
        ? shipProvince
        : getProvincePayload(fields.billCountry, fields.billProvince, fields.billProvinceText);
      const billingAddress = billingSame
        ? { ...shippingAddress }
        : {
            first_name: recipientFirst,
            last_name: recipientLast,
            company: addressCompany,
            address1: fields.billAddress1.value.trim(),
            address2: fields.billAddress2.value.trim(),
            zip: fields.billZip.value.trim(),
            city: fields.billCity.value.trim(),
            province: billProvince.province,
            province_code: billProvince.province_code,
            country_code: fields.billCountry.value,
            phone: composePhone(fields.billPhonePrefix, fields.billPhone),
          };

      const payload = {
        shop: form.dataset.shopDomain,
        attempt_id: ONBOARDING_ATTEMPT_ID,
        entry_point: getEntryPoint(),
        customer_id: form.dataset.customerId,
        country_code: countryCode,
        customer_type: payloadCustomerType,
        billing_customer_type: customerTypeLabel,
        first_name: fields.firstName.value.trim(),
        last_name: fields.lastName.value.trim(),
        company_name: company ? companyName : "",
        vat_number: business ? vatNumber : "",
        tax_code: italy && (!business || personBusiness) ? taxCode : "",
        company_tax_code: italy && company ? companyTaxCode : "",
        company_tax_code_same_as_vat: companyTaxCodeSameAsVatValue,
        sdi_code: italy && business ? sdiCode : "",
        eu_vat_id: !italy && business ? vatNumber : "",
        eu_country: !italy ? countryCode : "",
        country_scope: italy ? "IT" : "EU",
        billing_same_as_shipping: String(billingSame),
        newsletter_optin: Boolean(fields.newsletterOptin?.checked && !fields.newsletterOptin.disabled),
        shipping_address: shippingAddress,
        billing_address: billingAddress,
        metafields: {
          billing: {
            customer_type: customerTypeLabel,
            vat_number: italy && business ? vatNumber : "",
            tax_code: italy && (!business || personBusiness) ? taxCode : "",
            company_tax_code: italy && company ? companyTaxCode : "",
            company_tax_code_same_as_vat: companyTaxCodeSameAsVatValue,
            sdi_code: italy && business ? sdiCode : "",
            company_name: company ? companyName : "",
            eu_vat_id: !italy && business ? vatNumber : "",
            eu_country: !italy ? countryCode : "",
            country_scope: italy ? "IT" : "EU",
          },
        },
      };

      loader?.removeAttribute("hidden");
      submitButton.disabled = true;

      try {
        const response = await fetch(form.dataset.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || result.status !== "ok") {
          throw new Error(getEndpointErrorMessage(result));
        }

        publishAnalytics("guestside:fiscal_onboarding_complete", {
          onboarding_form: "completo",
          entry_point: payload.entry_point,
          customer_type: payload.billing_customer_type,
          country_code: payload.country_code,
          vies_status: result.vies_status || "",
        });

        const newsletterSubscribed = payload.newsletter_optin
          ? result.newsletter_subscribed === true
          : null;

        // Data is saved at this point. For an EU (non-Italian) business, surface the
        // VIES outcome before leaving: "invalid" (no reverse charge, country VAT) or
        // "unavailable" (technical failure, offer retry / manual verification).
        const viesStatus = result.vies_status;
        if (business && !italy && viesStatus === "invalid") {
          openViesModal("invalid");
          return;
        }
        if (business && !italy && viesStatus === "unavailable") {
          openViesModal("unavailable");
          return;
        }
        if (business && viesStatus === "valid" && newsletterSubscribed === null) {
          showMessage(form.dataset.messageViesValid, "success");
          setTimeout(proceed, 1500);
          return;
        }

        if (newsletterSubscribed === null) {
          // Shopify puo' impiegare un breve intervallo prima di esporre al tema
          // il tag appena aggiunto dal PIM. Manteniamo visibile il loader prima
          // del redirect per ridurre il rischio che il gate legga lo stato
          // precedente e rimandi il cliente nuovamente all'onboarding.
          await new Promise((resolve) => setTimeout(resolve, PROFILE_PROPAGATION_DELAY_MS));
          proceed();
        } else {
          // Give the user a moment to actually see the newsletter outcome before
          // the page navigates away — otherwise the message flashes and is never read.
          showMessage(
            newsletterSubscribed ? form.dataset.messageNewsletterSuccess : form.dataset.messageNewsletterError,
            newsletterSubscribed ? "success" : "error"
          );
          setTimeout(proceed, 1500);
        }
      } catch (error) {
        // Gli errori applicativi hanno gia' un ViesCheckLog creato dal backend;
        // segnaliamo qui soltanto quelli che non sono riusciti ad arrivarci.
        if (error.name === "TypeError") {
          reportOnboardingError(form.dataset.endpoint, form.dataset.shopDomain, "full_submit_request", error);
        }
        const message =
          error.name === "TypeError"
            ? form.dataset.messageNetworkError
            : error.message;
        showMessage(message, "error");
      } finally {
        loader?.setAttribute("hidden", "");
        submitButton.disabled = false;
      }
    }

    // VIES restituisce ragione sociale e sede insieme all'esito della verifica.
    // Chiederle a chi ha appena scritto la partita IVA e' lavoro inutile: qui si
    // riempie solo cio' che e' rimasto vuoto, senza mai sovrascrivere quello che
    // l'utente ha gia' digitato. Germania e Spagna non espongono questi dati, e
    // in quel caso semplicemente non arriva nulla.
    let ultimaPartitaIvaCercata = "";
    // Quella precompilata dal profilo: finche' il cliente non la cambia, la
    // ragione sociale salvata resta sua e non va toccata. defaultValue e non
    // value: ricaricando la pagina Chrome rimette nel campo il numero digitato,
    // che sembrava cosi' "gia' salvato" e la sede del VIES non veniva scritta.
    const partitaIvaSalvata = normalizeVatNumber(fields.vatNumber?.defaultValue, fields.country?.value);
    let lookupInCorso = null;
    let lookupAutomaticoTimer = null;
    // I campi indirizzo restano chiusi finche' non sappiamo cosa dice il VIES:
    // per le aziende li compiliamo noi, e mostrarli prima significa farli
    // riempire a mano per poi cambiarglieli sotto. Una volta aperti non si
    // richiudono piu': l'unico rischio inaccettabile e' un modulo senza campi.
    let campiSbloccati = false;

    function campiVisibili() {
      return campiSbloccati || !isBusiness();
    }

    function sbloccaCampi() {
      if (campiSbloccati) return;
      campiSbloccati = true;
      syncFields();
    }

    function riempiSeVuoto(campo, valore) {
      if (!campo || !valore) return;
      if (campo.value.trim()) return;
      campo.value = valore;
    }

    // La sede VIES e' testo libero su piu' righe. Si prova solo il formato
    // italiano "VIA ROMA 1 / 39100 BOLZANO BZ": se non combacia si lascia stare,
    // meglio nessun suggerimento che un indirizzo sbagliato da correggere.
    // Il VIES scrive tutto in maiuscolo ("VIA SEBASTIAN ALTMANN 12") e cosi'
    // finiva in fattura. Iniziali maiuscole, preposizioni minuscole, numeri
    // romani e sigle con i punti come sono.
    const PREPOSIZIONI = new Set(["di", "del", "della", "dello", "dei", "degli", "delle", "da", "dal", "dalla", "e", "ed", "al", "alla", "allo", "ai", "agli", "alle", "in", "sul", "sulla"]);
    function inMinuscolo(testo) {
      return String(testo || "")
        .toLowerCase()
        .split(/(\s+)/)
        .map((parola, i) => {
          if (/^\s+$/.test(parola) || !parola) return parola;
          if (i > 0 && PREPOSIZIONI.has(parola)) return parola;
          const maiuscola = parola.toUpperCase();
          if (/^[ivxlcdm]{2,}$/.test(parola) && /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/.test(maiuscola)) return maiuscola;
          const conIniziali = parola.replace(/(^|['’\-/])(\p{L})/gu, (m, sep, lettera) => sep + lettera.toUpperCase());
          // "D'Alfaedo", "Nell'Emilia": la preposizione elisa resta minuscola.
          if (i > 0) return conIniziali.replace(/^(\p{L}+)(['’])/u, (m, prep, apo) => PREPOSIZIONI.has(prep.toLowerCase()) || /^(d|l|dell|nell|all|dall|sull)$/i.test(prep) ? prep.toLowerCase() + apo : m);
          return conIniziali;
        })
        .join("");
    }

    function sedeItaliana(testo) {
      const righe = String(testo || "")
        .split(/[\n\r]+/)
        .map((r) => r.trim())
        .filter(Boolean);
      if (righe.length < 2) return null;
      const ultima = righe[righe.length - 1].match(/^(\d{5})\s+(.+?)\s+([A-Z]{2})$/);
      if (!ultima) return null;
      return {
        via: inMinuscolo(righe.slice(0, -1).join(" ")),
        cap: ultima[1],
        // I comuni bilingui arrivano come "BOLZANO .BOZEN.": il nome tedesco fra
        // punti e' una convenzione del registro, non va nell'indirizzo.
        citta: inMinuscolo(ultima[2].replace(/\s+\.[^.]+\.?$/, "").trim()),
        provincia: ultima[3],
      };
    }

    async function precompilaDaVies(force = false) {
      if (!isBusiness() || !form.dataset.endpoint) return;
      const paese = fields.country?.value || "";
      const numero = normalizeVatNumber(fields.vatNumber?.value, paese);
      if (!numero || (!force && numero === ultimaPartitaIvaCercata)) return;
      ultimaPartitaIvaCercata = numero;
      // Si toglie l'esito precedente solo quando parte una ricerca vera: il
      // clic su "Verifica" e l'uscita dal campo la chiedono entrambi, e la
      // seconda (stesso numero, saltata) cancellava l'avviso appena mostrato.
      avvisoVerificaIva("");

      const attesa = form.querySelector("[data-vat-lookup]");
      attesa?.removeAttribute("hidden");
      try {
        await eseguiLookup(paese, numero);
      } finally {
        attesa?.setAttribute("hidden", "");
        // Qualunque cosa sia successa — trovato, non trovato, VIES giu', paese
        // che non pubblica gli indirizzi — da qui in poi i campi si vedono.
        sbloccaCampi();
      }
    }

    function formatoVatPronto() {
      const paese = fields.country?.value || "";
      const numero = normalizeVatNumber(fields.vatNumber?.value, paese);
      if (paese === "IT") return partitaIvaValida(numero);
      return vatUeValido(numero, paese);
    }

    // Esito della verifica sotto il campo partita IVA. Prima un numero
    // inesistente o un PIM irraggiungibile non dicevano nulla, e il cliente non
    // capiva perche' ragione sociale e sede restassero vuote.
    function avvisoVerificaIva(testo) {
      const contenitore = fields.vatNumber?.closest(".fiscal-onboarding__field");
      if (!contenitore) return;
      let avviso = contenitore.querySelector("[data-vat-notice]");
      if (!testo) {
        avviso?.remove();
        return;
      }
      if (!avviso) {
        avviso = document.createElement("small");
        avviso.className = "fiscal-onboarding__field-help fiscal-onboarding__field-help--warning";
        avviso.setAttribute("data-vat-notice", "");
        avviso.setAttribute("role", "status");
        contenitore.appendChild(avviso);
      }
      avviso.textContent = testo;
    }

    function avviaLookupVat({ force = false, validate = false } = {}) {
      if (lookupInCorso) return lookupInCorso;
      if (validate && !formatoVatPronto()) {
        const paese = fields.country?.value;
        const numero = normalizeVatNumber(fields.vatNumber?.value, paese);
        const testo = paese !== "IT"
          ? form.dataset.messageVatEuInvalid
          : /^\d{11}$/.test(numero)
            ? form.dataset.messageVatItChecksum
            : form.dataset.messageVatItInvalid;
        showMessage(testo, "error");
        showFieldError(fields.vatNumber, testo);
        return Promise.resolve();
      }
      showMessage("", "");
      if (vatLookupButton) {
        vatLookupButton.disabled = true;
        vatLookupButton.textContent = vatLookupButton.dataset.busyLabel;
      }
      lookupInCorso = precompilaDaVies(force).finally(() => {
        lookupInCorso = null;
        if (vatLookupButton) {
          vatLookupButton.disabled = false;
          vatLookupButton.textContent = vatLookupButton.dataset.idleLabel;
        }
        // Anche se VIES non restituisce dati, l'utente deve poter continuare
        // compilando manualmente il resto del modulo.
        sbloccaCampi();
      });
      return lookupInCorso;
    }

    async function eseguiLookup(paese, numero) {
      let esito;
      try {
        const risposta = await fetch(form.dataset.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            stage: "lookup",
            attempt_id: ONBOARDING_ATTEMPT_ID,
            shop: form.dataset.shopDomain,
            country_code: paese,
            vat_number: numero,
            eu_country: paese,
            eu_vat_id: numero,
          }),
        });
        esito = await risposta.json();
        if (!risposta.ok) {
          reportOnboardingError(
            form.dataset.endpoint,
            form.dataset.shopDomain,
            "vat_lookup_http",
            new Error("HTTP lookup"),
            risposta.status
          );
          avvisoVerificaIva(form.dataset.messageVatLookupUnavailable);
          return;
        }
      } catch (err) {
        reportOnboardingError(form.dataset.endpoint, form.dataset.shopDomain, "vat_lookup_network", err);
        // E' un aiuto, non un requisito: il cliente compila a mano, ma lo sa.
        avvisoVerificaIva(form.dataset.messageVatLookupUnavailable);
        return;
      }
      if (esito && esito.vies_valid === false) {
        avvisoVerificaIva(form.dataset.messageVatNotFound);
      } else if (esito && esito.vies_valid !== true) {
        avvisoVerificaIva(form.dataset.messageVatLookupUnavailable);
      }
      // Il VIES italiano appende a volte una sigla dopo "!!" (es.
      // "AMK S.R.L. !!AMK"): e' un residuo della loro banca dati, non fa parte
      // della ragione sociale e in fattura non ci deve finire.
      const ragioneSociale = esito && esito.vies_valid === true
        ? String(esito.vies_name || "").split("!!")[0].trim()
        : "";

      // P.IVA diversa da quella salvata: la ragione sociale precompilata dal
      // profilo appartiene all'azienda precedente. Si sostituisce con quella del
      // VIES o, se il VIES non la fornisce (Germania, Spagna), si svuota perche'
      // il cliente la scriva: meglio un campo obbligatorio vuoto che la fattura
      // intestata a un'altra azienda.
      if (fields.companyName && numero !== partitaIvaSalvata) {
        fields.companyName.value = ragioneSociale;
      }
      if (!esito || esito.vies_valid !== true) return;
      riempiSeVuoto(fields.companyName, ragioneSociale);
      controllaDittaIndividuale();

      const sede = paese === "IT" ? sedeItaliana(esito.vies_address) : null;
      if (!sede) return;

      // La sede legale e' l'indirizzo di FATTURAZIONE, non quello di consegna:
      // dove arriva la merce e' l'unica cosa che il VIES non puo' sapere.
      // Riempiva la spedizione, che pero' e' gia' precompilata dall'indirizzo
      // Shopify del cliente: riempiSeVuoto non scriveva nulla e la sede si
      // perdeva in silenzio. Poi la spunta "uguale alla spedizione" la
      // sostituiva con l'indirizzo di consegna (caso Imhold, 24/08/2026).
      // Senza ancora una spedizione (cliente nuovo) la merce va alla sede: la
      // spunta "usa anche come spedizione" resta, invece di mostrare due
      // riquadri con lo stesso indirizzo.
      const spedizioneVuota = ![fields.shipAddress1, fields.shipZip, fields.shipCity]
        .some((campo) => campo?.value.trim());
      if (!spedizioneVuota && !sedeUgualeAllaSpedizione(sede) && isBillingSameAsShipping()) {
        fields.billingSameAsShipping.checked = false;
        spuntaFatturazioneDecisa = true;
        // Da chiamare a mano: assegnare .checked non emette l'evento change, e
        // senza questa i riquadri restano come stavano.
        syncAddressLayout();
      }

      // La sede riconosciuta e' sempre italiana. Il paese di fatturazione nasce
      // uguale a quello dell'indirizzo di spedizione del profilo (Liquid), quindi
      // non e' mai vuoto: un controllo "solo se vuoto" lasciava la sede di
      // Bolzano col paese di spedizione (es. DE) e Shopify la salvava cosi'.
      if (fields.billCountry && fields.billCountry.value !== "IT") {
        fields.billCountry.value = "IT";
        syncPhonePrefix(fields.billPhonePrefix, fields.billCountry);
        syncProvinceControls(fields.billCountry, fields.billProvince, fields.billProvinceText, false);
      }
      // P.IVA nuova: la sede ufficiale sostituisce la fatturazione precedente,
      // come la ragione sociale. Con "solo se vuoto" un privato che diventava
      // azienda teneva in fattura l'indirizzo di casa (prova del 14/10/2026).
      // Stessa P.IVA gia' salvata: l'indirizzo l'ha scelto il cliente, resta.
      const scriviSede = numero !== partitaIvaSalvata
        ? (campo, valore) => { if (campo && valore) campo.value = valore; }
        : riempiSeVuoto;
      scriviSede(fields.billAddress1, sede.via);
      if (numero !== partitaIvaSalvata && fields.billAddress2) fields.billAddress2.value = "";
      scriviSede(fields.billZip, sede.cap);
      scriviSede(fields.billCity, sede.citta);
      selezionaProvincia(fields.billProvince, sede.provincia, { forza: numero !== partitaIvaSalvata });
      // Il telefono di fatturazione diventa obbligatorio quando la fatturazione
      // e' separata, ma il VIES non lo fornisce: e' la stessa azienda, quindi si
      // copia da quello di spedizione invece di farlo ridigitare.
      riempiSeVuoto(fields.billPhone, fields.shipPhone?.value);
      syncPhonePrefix(fields.billPhonePrefix, fields.billCountry);

      // Cliente senza indirizzo su Shopify: la sede e' il miglior punto di
      // partenza anche per la consegna. Puo' cambiarla liberamente, ormai la
      // fatturazione e' un campo suo e non la segue piu'.
      riempiSeVuoto(fields.shipAddress1, sede.via);
      riempiSeVuoto(fields.shipZip, sede.cap);
      riempiSeVuoto(fields.shipCity, sede.citta);
      selezionaProvincia(fields.shipProvince, sede.provincia);
    }

    // CAP e comune, non la via: il VIES scrive "VIA LARGA 31" dove il cliente
    // scrive "Via Larga, 31", e confrontare la via darebbe differenze inventate.
    function sedeUgualeAllaSpedizione(sede) {
      const norm = (v) => String(v || "").trim().toLowerCase();
      return (
        norm(sede.cap) === norm(fields.shipZip?.value) &&
        norm(sede.citta) === norm(fields.shipCity?.value)
      );
    }

    // La provincia e' una select: assegnare un codice che non esiste fra le
    // opzioni la lascerebbe vuota, e il campo e' obbligatorio.
    function selezionaProvincia(select, codice, { forza = false } = {}) {
      if (!select || !codice || (select.value && !forza)) return;
      if (select.querySelector(`option[value="${codice}"]`)) select.value = codice;
    }

    // La ricerca parte all'uscita dal campo, e cliccare "Salva" fa uscire dal
    // campo: senza tenerne traccia il salvataggio correrebbe contro la risposta
    // del PIM e partirebbe con lo stato vecchio del modulo, oppure si vedrebbe
    // spuntare la fatturazione a invio gia' avvenuto. submit() la aspetta.
    fields.vatNumber?.addEventListener("blur", (event) => {
      // Toccando il pulsante esplicito lasciamo che sia il suo handler a
      // validare il formato prima della chiamata, senza far partire il fallback.
      if (event.relatedTarget === vatLookupButton) return;
      avviaLookupVat();
    });
    vatLookupButton?.addEventListener("click", () => {
      avviaLookupVat({ force: true, validate: true });
    });
    fields.vatNumber?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      avviaLookupVat({ force: true, validate: true });
    });
    fields.vatNumber?.addEventListener("input", () => {
      window.clearTimeout(lookupAutomaticoTimer);
      const paese = fields.country?.value || "";
      const numero = normalizeVatNumber(fields.vatNumber?.value, paese);
      if (paese !== "IT" || !/^\d{11}$/.test(numero)) return;
      // Un numero con la cifra di controllo sbagliata non si cerca: lo dice
      // subito il campo, senza aspettare il VIES.
      if (!partitaIvaValida(numero)) {
        showFieldError(fields.vatNumber, form.dataset.messageVatItChecksum, { scroll: false });
        return;
      }
      lookupAutomaticoTimer = window.setTimeout(() => avviaLookupVat(), 600);
    });

    // La nazione del profilo e' il punto di partenza degli indirizzi ancora da
    // scrivere: un'azienda tedesca non deve trovarsi "Italia" e la provincia
    // obbligatoria. Un indirizzo gia' compilato non si tocca.
    fields.country?.addEventListener("change", () => {
      const paese = fields.country.value;
      [
        [fields.shipCountry, fields.shipAddress1],
        [fields.billCountry, fields.billAddress1],
      ].forEach(([select, via]) => {
        if (!select || via?.value.trim() || select.value === paese) return;
        if (![...select.options].some((opzione) => opzione.value === paese)) return;
        select.value = paese;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      });
      syncFields();
    });
    fields.type?.addEventListener("change", syncFields);
    // Anche "change": l'autocompilazione del browser cambia nome e cognome
    // senza passare dal campo, e l'avviso restava quello di prima.
    [fields.taxCode, fields.firstName, fields.lastName].forEach((campo) => {
      campo?.addEventListener("blur", controllaNominativoCodiceFiscale);
      campo?.addEventListener("change", controllaNominativoCodiceFiscale);
    });
    [fields.companyName, fields.firstName, fields.lastName].forEach((campo) => {
      campo?.addEventListener("blur", controllaDittaIndividuale);
      campo?.addEventListener("change", controllaDittaIndividuale);
    });
    // Correggere un campo toglie il suo errore.
    form.addEventListener("input", (event) => clearFieldError(event.target));
    form.addEventListener("change", (event) => clearFieldError(event.target));
    fields.companyTaxCodeDiffers?.addEventListener("change", syncFields);
    fields.billingSameAsShipping?.addEventListener("change", () => {
      spuntaFatturazioneDecisa = true;
      syncFields();
    });
    fields.shipPhonePrefix?.addEventListener("change", () => {
      fields.shipPhonePrefix.dataset.userSet = "true";
    });
    fields.billPhonePrefix?.addEventListener("change", () => {
      fields.billPhonePrefix.dataset.userSet = "true";
    });
    // La provincia appartiene al paese di prima: cambiando paese si svuota, se no
    // un indirizzo di Monaco partirebbe con "Bolzano". Solo sul cambio fatto dal
    // cliente: il precompilato VIES scrive i campi senza generare "change".
    function clearProvince(provinceSelect, provinceText) {
      if (provinceSelect) provinceSelect.value = "";
      if (provinceText) provinceText.value = "";
    }

    // Dopo il cambio paese decide syncAddressLayout quale riquadro e' attivo.
    // Prima la provincia di fatturazione si spegneva quando la spunta era
    // attiva: giusto per un privato, ma per un'azienda italiana il riquadro
    // visibile con la spunta e' proprio la sede, e la provincia restava grigia
    // e vuota, senza modo di salvare.
    fields.shipCountry?.addEventListener("change", () => {
      syncPhonePrefix(fields.shipPhonePrefix, fields.shipCountry);
      clearProvince(fields.shipProvince, fields.shipProvinceText);
      syncProvinceControls(fields.shipCountry, fields.shipProvince, fields.shipProvinceText, false);
      syncAddressLayout();
    });
    fields.billCountry?.addEventListener("change", () => {
      syncPhonePrefix(fields.billPhonePrefix, fields.billCountry);
      clearProvince(fields.billProvince, fields.billProvinceText);
      syncAddressLayout();
    });
    fields.vatNumber?.addEventListener("input", syncCompanyTaxCode);
    fields.vatNumber?.addEventListener("blur", () => {
      if (!isBusiness()) return;
      fields.vatNumber.value = normalizeVatNumber(fields.vatNumber.value, fields.country.value);
      syncCompanyTaxCode();
    });
    form.addEventListener("submit", submit);
    // Azienda senza partita IVA: il browser fermava "Salva" con il suo fumetto
    // generico ("Compila questo campo") sul campo, senza dire che e' lei ad
    // aprire il resto del modulo. Il messaggio nostro lo dice.
    submitButton?.addEventListener("click", (event) => {
      if (!isBusiness() || (fields.vatNumber?.value || "").trim()) return;
      event.preventDefault();
      const testo = form.dataset.messageVatRequired || form.dataset.messageValidationFailed;
      showMessage(testo, "error");
      showFieldError(fields.vatNumber, testo);
    });
    form.addEventListener("focusin", (event) => {
      const inAddress = event.target.closest(".fiscal-onboarding__address, .fiscal-onboarding__billing");
      setProgress(inAddress ? 2 : 1);
    });

    viesModal?.querySelectorAll("[data-vies-proceed]").forEach((btn) => btn.addEventListener("click", proceed));
    viesModal?.querySelectorAll("[data-vies-dismiss]").forEach((btn) => btn.addEventListener("click", closeViesModal));
    viesModal?.querySelectorAll("[data-vies-retry]").forEach((btn) =>
      btn.addEventListener("click", () => {
        closeViesModal();
        form.requestSubmit();
      })
    );
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && viesModal && !viesModal.hidden) closeViesModal();
      if (event.key !== "Tab" || !viesModal || viesModal.hidden) return;
      const dialog = viesModal.querySelector('[role="dialog"]');
      const focusable = Array.from(
        dialog?.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href]') || []
      ).filter((element) => !element.closest("[hidden]"));
      if (!focusable.length) {
        event.preventDefault();
        dialog?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Shopify reloads the page after the contact form posts; reopen the modal so the
    // customer sees the "request sent" confirmation.
    if (new URLSearchParams(window.location.search).get("contact_posted") === "true") {
      openViesModal("unavailable");
    }

    // Modulo riaperto: la fatturazione dichiarata l'ultima volta (salvata dal PIM
    // in billing.billing_address) torna nei suoi campi. Senza, i campi erano vuoti
    // e la spunta "coincide con la spedizione" sovrascriveva la residenza o la
    // sede al primo salvataggio. Si ricompila anche quando coincide con la
    // spedizione: l'azienda italiana vede la sede per prima, e vuota sarebbe da
    // riscrivere; la spunta resta selezionata.
    function precompilaFatturazioneSalvata() {
      let salvata = null;
      try { salvata = JSON.parse(form.dataset.savedBilling || "null"); } catch (err) { salvata = null; }
      if (!salvata || !salvata.address1 || !salvata.city || !fields.billingSameAsShipping) return;

      const norm = (v) => String(v || "").trim().toLowerCase();
      const ugualeAllaSpedizione =
        norm(salvata.address1) === norm(fields.shipAddress1?.value) &&
        norm(salvata.address2) === norm(fields.shipAddress2?.value) &&
        norm(salvata.zip) === norm(fields.shipZip?.value) &&
        norm(salvata.city) === norm(fields.shipCity?.value) &&
        norm(salvata.country_code) === norm(fields.shipCountry?.value);

      if (fields.billCountry && salvata.country_code
        && fields.billCountry.querySelector(`option[value="${salvata.country_code}"]`)) {
        fields.billCountry.value = salvata.country_code;
      }
      if (fields.billAddress1) fields.billAddress1.value = salvata.address1 || "";
      if (fields.billAddress2) fields.billAddress2.value = salvata.address2 || "";
      if (fields.billZip) fields.billZip.value = salvata.zip || "";
      if (fields.billCity) fields.billCity.value = salvata.city || "";
      if (fields.billCountry?.value === "IT") {
        if (fields.billProvince?.querySelector(`option[value="${salvata.province_code}"]`)) {
          fields.billProvince.value = salvata.province_code;
        }
      } else if (fields.billProvinceText) {
        fields.billProvinceText.value = salvata.province || "";
      }

      // Il telefono e' salvato col prefisso ("+39 333..."): si riporta nel menu
      // del prefisso il piu' lungo che combacia, il resto va nel numero.
      const telefono = String(salvata.phone || "").trim();
      const prefissi = [...(fields.billPhonePrefix?.options || [])]
        .map((option) => option.value)
        .filter((value) => value && telefono.startsWith(value))
        .sort((a, b) => b.length - a.length);
      if (prefissi.length) {
        fields.billPhonePrefix.value = prefissi[0];
        fields.billPhonePrefix.dataset.userSet = "true";
        if (fields.billPhone) fields.billPhone.value = telefono.slice(prefissi[0].length).trim();
      } else if (fields.billPhone) {
        fields.billPhone.value = telefono;
      }

      fields.billingSameAsShipping.checked = ugualeAllaSpedizione;
      spuntaFatturazioneDecisa = true;
    }
    precompilaFatturazioneSalvata();

    // Cliente che torna con la partita IVA gia' salvata: non c'e' nessuna
    // ricerca da attendere, quindi non ha senso nascondergli gli indirizzi.
    if ((fields.vatNumber?.value || "").trim()) {
      campiSbloccati = true;
    }

    syncFields();
  }

  // Fascia e barra di navigazione mobile del tema sono entrambe fixed in fondo,
  // quindi si accavallano. A spostarsi e' la barra, non la fascia.
  //
  // L'avevo fatto al contrario, ed era la scelta sbagliata: la fascia e' cio'
  // che l'utente sta leggendo, e ogni riassestamento gli si muoveva sotto gli
  // occhi. Per giunta inseguiva un elemento che cambia da solo — cresce quando
  // arrivano i font, sparisce e torna con lo scroll — quindi ogni cambiamento
  // di quello diventava un salto di questa.
  //
  // Invertito: la fascia sta ferma a bottom 0 e non si muove mai. La barra si
  // scosta di quanto misura la fascia, e siccome la barra compare e sparisce di
  // suo mentre si scorre, un suo riposizionamento non si nota. Qui misuriamo un
  // elemento nostro, alto quanto il suo contenuto e stabile.
  // L'header del tema e' agganciato in alto: la colonna laterale deve fermarsi
  // sotto di lui, non scorrergli sotto. Il tema non ne espone l'altezza
  // (--height-sticky e' la barra "aggiungi al carrello" delle schede prodotto,
  // non l'header), quindi la misuriamo e la scriviamo come variabile.
  function initHeaderOffset() {
    const aside = document.querySelector(".fiscal-onboarding__aside");
    if (!aside) return;
    const root = document.documentElement;

    function sync() {
      const header = document.getElementById("sticky-header")
        || document.querySelector("sticky-header, .header-wrapper, .shopify-section--header");
      const altezza = header ? Math.round(header.getBoundingClientRect().height) : 0;
      root.style.setProperty("--fo-header-offset", `${altezza}px`);
    }

    sync();
    window.addEventListener("resize", sync);
    // L'header cambia altezza quando si aggancia scorrendo, e i font caricano
    // dopo il DOM: rimisurare al load e allo scroll evita un valore vecchio.
    window.addEventListener("load", sync);
    window.addEventListener("scroll", sync, { passive: true });
  }

  function initBannerOffset() {
    const root = document.documentElement;
    const banner = document.querySelector("[data-fiscal-banner]");
    if (!banner) {
      root.style.setProperty("--fo-banner-height", "0px");
      return;
    }

    let queued = false;
    function sync() {
      queued = false;
      // Nascosta dal mini-modulo aperto, o non ancora mostrata: la barra torna
      // a filo del fondo senza che serva un caso a parte nel CSS.
      const height = banner.hidden ? 0 : Math.round(banner.getBoundingClientRect().height);
      root.style.setProperty("--fo-banner-height", `${height}px`);
    }
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    }

    sync();
    // Cambia altezza quando il testo va a capo su schermi stretti, o quando i
    // font sostituiscono quelli di sistema.
    if (typeof ResizeObserver === "function") {
      new ResizeObserver(schedule).observe(banner);
    }
    // hidden viene tolto e rimesso da initPricingOverlay aprendo e chiudendo il
    // mini-modulo.
    new MutationObserver(schedule).observe(banner, {
      attributes: true,
      attributeFilter: ["hidden", "class", "style"],
    });
    window.addEventListener("resize", schedule);
  }

  document.addEventListener("DOMContentLoaded", () => {
    initCurrentPageLoginLinks();
    if (initCheckoutAfterLogin()) return;
    initCheckoutReturnRedirect();
    initPricingOverlay();
    initCheckoutGate();
    initOnboardingForm();
    initBannerOffset();
    initHeaderOffset();
  });
})();
